import { Button, Calendar, cn } from "@tellescope/ui";
import { CommonCalendarOptions, DateRange } from "../../types";
import { CalendarFilters } from "../../filters";
import { isWithinInterval } from "date-fns/isWithinInterval";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import { getZonedDayStart } from "../../utils";
import { DndContext } from "@dnd-kit/core";
import { CurrentTimeLine } from "../CurrentTimeLine";
import { TimeColumn, TimeColumnHeader } from "../TimeColumn";
import { DAY_COLUMN, DAY_VIEW } from "../constants";
import { EventCreateDialog, UnavailableUsersWarningDialog } from "../dialogs";
import { useDayColumnDragLogic } from "./useDayColumnDragLogic";
import { DayEventsColumn } from "./DayEventsColumn";
import { useCalendarDayViewLogic } from "./useCalendarDayViewLogic";
import { Link } from "react-router-dom";
import { OngoingAndUpcomingEvents } from "./OngoingAndUpcomingEvents";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { HiddenEventsExpander } from "../HiddenEventsExpander";

export interface CalendarDayViewProps extends DateRange, CommonCalendarOptions {
  className?: string;
  getOnDayClick?: (date: Date) => (() => void) | string;
  filters?: CalendarFilters;
  currentUserId?: string;
  showAvailability?: boolean;
}

export const CalendarDayView = ({
  startDate,
  endDate,
  referenceDate,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  className,
  getOnDayClick,
  showAvailability,
  filters,
  currentUserId,
}: CalendarDayViewProps) => {
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const {
    browserTimezone,
    showTwoTimezones,
    eventsByDate,
    scrollContainerRef,
    thumbRef,
    handleScroll,
    headersRef,
    creationMetadata,
    handleCreateClick,
    handleGuardedCreateClick,
    creationWarningDialogProps,
    selectedUserIds,
    closeCreation,
    selectedMonth,
    setSelectedMonth,
    dayStartMinutes,
    dayEndMinutes,
    hasEarlierHiddenEvents,
    hasLaterHiddenEvents,
    handleExpandEarlier,
    handleExpandLater,
    isNearTop,
    isNearBottom,
  } = useCalendarDayViewLogic({
    startDate,
    endDate,
    referenceDate,
    timezone,
    className,
    getOnDayClick,
    filters,
    currentUserId,
  });

  const {
    sensors,
    activeDrag,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
    updateWarningDialogProps,
  } = useDayColumnDragLogic({ startDate, timezone });

  return (
    <section
      data-testid="calendar-view-day"
      className={cn("@container flex flex-col min-h-0 overflow-hidden flex-grow", className)}
    >
      <div className="flex flex-grow min-h-0 overflow-hidden w-full">
        <div className="relative overflow-hidden flex-grow">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="relative h-full w-full overflow-y-auto [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none" }}
          >
            <div
              ref={headersRef}
              className="sticky top-0 z-[1000] bg-opacity-70 bg-white backdrop-blur-sm flex flex-col"
            >
              <div
                className="grid divide-x bg-opacity-0"
                style={{ gridTemplateColumns: DAY_VIEW.GRID_TEMPLATE_COLUMNS }}
              >
                <TimeColumnHeader
                  startDate={startDate}
                  timezone={timezone}
                  browserTimezone={browserTimezone}
                  showTwoTimezones={showTwoTimezones}
                />
                <div className="flex items-center justify-center py-2 text-xs font-light text-muted-foreground border-b border-b-gray-200 gap-2">
                  {formatInTimeZone(startDate, timezone, "EEE")}
                  <span className="text-primary font-medium">{formatInTimeZone(startDate, timezone, "d")}</span>
                </div>
              </div>
              {hasEarlierHiddenEvents && (
                <HiddenEventsExpander
                  direction="earlier"
                  onClick={handleExpandEarlier}
                  visible={isNearTop}
                  className="-bottom-8 hover:opacity-100"
                />
              )}
            </div>
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <div className="relative grid divide-x" style={{ gridTemplateColumns: DAY_VIEW.GRID_TEMPLATE_COLUMNS }}>
                <TimeColumn
                  startDate={startDate}
                  timezone={timezone}
                  browserTimezone={browserTimezone}
                  showTwoTimezones={showTwoTimezones}
                  dayStartMinutes={dayStartMinutes}
                  dayEndMinutes={dayEndMinutes}
                />
                <DayEventsColumn
                  droppableId="day-view-column"
                  droppableData={{ dayIndex: 0 }}
                  dayStartDate={startDate}
                  events={eventsByDate[startDate.getTime()]}
                  onTimeClick={handleGuardedCreateClick}
                  onCreateRange={handleGuardedCreateClick}
                  activeDrag={activeDrag}
                  timezone={timezone}
                  dayStartMinutes={dayStartMinutes}
                  dayEndMinutes={dayEndMinutes}
                  userIds={filters?.userIds}
                  showAvailability={showAvailability}
                />
                <CurrentTimeLine
                  shouldDisplay={(date) => {
                    if (!isWithinInterval(date, { start: startDate, end: endDate })) return false;
                    const minutesIntoDay = differenceInMinutes(date, getZonedDayStart(date, timezone));
                    return minutesIntoDay >= dayStartMinutes && minutesIntoDay <= dayEndMinutes;
                  }}
                  timezone={timezone}
                  getTop={(date) =>
                    (differenceInMinutes(date, getZonedDayStart(date, timezone)) - dayStartMinutes) *
                    DAY_COLUMN.MINUTE_HEIGHT
                  }
                  textAvailableWidth={DAY_COLUMN.LEFT_COLUMN_WIDTH - 7}
                />
              </div>
            </DndContext>
          </div>
          <div
            ref={thumbRef}
            className="absolute top-0 right-1 w-1.5 bg-black/40 rounded-full pointer-events-none opacity-0 transition-opacity duration-300 z-[10000]"
          />
          {hasLaterHiddenEvents && (
            <HiddenEventsExpander
              direction="later"
              onClick={handleExpandLater}
              visible={isNearBottom}
              className="bottom-2"
            />
          )}
        </div>
        <EventCreateDialog
          open={!!creationMetadata?.isCreating}
          onOpenChange={(open) => {
            if (open) {
              handleCreateClick(creationMetadata?.initialTimestamp ?? new Date().getTime());
            } else {
              closeCreation();
            }
          }}
          onSuccess={closeCreation}
          timezone={timezone}
          initialFormValues={
            creationMetadata?.initialTimestamp
              ? {
                  startDate: new Date(creationMetadata.initialTimestamp),
                  duration: creationMetadata.initialDurationMinutes ?? 15,
                  userIds: selectedUserIds.length ? selectedUserIds : undefined,
                }
              : undefined
          }
        />
        <UnavailableUsersWarningDialog {...creationWarningDialogProps} />
        <UnavailableUsersWarningDialog {...updateWarningDialogProps} />
        <div className="hidden @3xl:flex w-64 border-l flex-col">
          <Calendar
            mode="single"
            selected={startDate}
            month={selectedMonth}
            onMonthChange={setSelectedMonth}
            components={
              getOnDayClick
                ? {
                    Day: ({ day }) => {
                      const onClick = getOnDayClick(day.date);
                      const isSelected =
                        day.date.getFullYear() === startDate.getFullYear() &&
                        day.date.getMonth() === startDate.getMonth() &&
                        day.date.getDate() === startDate.getDate();
                      const isOtherMonth = day.date.getMonth() !== startDate.getMonth();
                      if (typeof onClick === "string") {
                        return (
                          <Button
                            variant={isSelected ? "default" : "ghost"}
                            asChild
                            className={cn("w-8 h-8 mr-[1.5px] font-light", isOtherMonth && "text-muted-foreground")}
                          >
                            <Link to={onClick}>{day.date.getDate()}</Link>
                          </Button>
                        );
                      }

                      return (
                        <Button
                          variant={isSelected ? "default" : "ghost"}
                          onClick={onClick}
                          className={cn("w-8 h-8 mr-[1.5px] font-light", isOtherMonth && "text-muted-foreground")}
                        >
                          {day.date.getDate()}
                        </Button>
                      );
                    },
                  }
                : undefined
            }
            className="w-full border-b"
          />
          <OngoingAndUpcomingEvents timezone={timezone} />
        </div>
      </div>
    </section>
  );
};
